import type { SupabaseClient } from '@supabase/supabase-js';
import type { Goal, Habit, GoalType } from '@/types/types';

export async function getAllGoals(client: SupabaseClient, userId: string): Promise<Goal[]> {
    const { data } = await client.from('goals').select('*').eq('user_id', userId);
    return data || [];
}

export async function addGoal(client: SupabaseClient, userId: string, goal: Goal): Promise<void> {
    const { error } = await client.from('goals').insert({
        id: goal.id,
        user_id: userId,
        cycle_id: goal.cycle_id,
        title: goal.title,
        description: goal.description,
        type: goal.type,
        subtasks: goal.subtasks?.map((s) => ({ ...s, name: s.name, done: s.done })),
        linked_habit_id: goal.linked_habit_id,
        current_streak: goal.current_streak,
        sort_order: goal.sort_order ?? 0
    });
    if (error) throw error;
}

export async function updateGoal(client: SupabaseClient, userId: string, updatedGoal: Goal): Promise<void> {
    const { error } = await client.from('goals').update({
        title: updatedGoal.title,
        description: updatedGoal.description,
        type: updatedGoal.type,
        cycle_id: updatedGoal.cycle_id,
        subtasks: updatedGoal.subtasks,
        current_streak: updatedGoal.current_streak,
        linked_habit_id: updatedGoal.linked_habit_id,
        sort_order: updatedGoal.sort_order
    }).eq('id', updatedGoal.id).eq('user_id', userId);
    if (error) throw error;
}

export async function deleteGoal(client: SupabaseClient, userId: string, goalId: string): Promise<void> {
    const { error } = await client.from('goals').delete().eq('id', goalId).eq('user_id', userId);
    if (error) throw error;
}

export async function reorderGoals(client: SupabaseClient, userId: string, orderedIds: string[]): Promise<void> {
    await Promise.all(orderedIds.map((id, index) =>
        client.from('goals').update({ sort_order: index }).eq('id', id).eq('user_id', userId)
    ));
}

export async function getNextGoalSortOrder(client: SupabaseClient, userId: string, cycleId: string): Promise<number> {
    const { data } = await client
        .from('goals').select('sort_order')
        .eq('cycle_id', cycleId).eq('user_id', userId)
        .order('sort_order', { ascending: false }).limit(1);
    return (data?.[0]?.sort_order ?? -1) + 1;
}

export async function carryForwardGoal(
    client: SupabaseClient,
    userId: string,
    goal: Goal,
    newCycleId: string,
    GoalTypeEnum: typeof GoalType,
): Promise<Goal & { needsHabitLink?: boolean; originalHabit?: Habit }> {
    const lineageId = goal.goal_lineage_id || goal.id;
    if (!goal.goal_lineage_id) {
        await client.from('goals').update({ goal_lineage_id: lineageId }).eq('id', goal.id);
    }
    const sortOrder = await getNextGoalSortOrder(client, userId, newCycleId);
    const newGoal: Goal & { needsHabitLink?: boolean; originalHabit?: Habit } = {
        id: crypto.randomUUID(),
        cycle_id: newCycleId,
        title: goal.title,
        description: goal.description,
        type: goal.type,
        subtasks: goal.type === GoalTypeEnum.TASK_PROJECT
            ? goal.subtasks?.map(s => ({ ...s, done: false }))
            : undefined,
        linked_habit_id: undefined,
        current_streak: 0,
        sort_order: sortOrder,
        goal_lineage_id: lineageId,
        priority: goal.priority,
    };

    if (goal.type === GoalTypeEnum.CONSISTENCY_METRIC && goal.linked_habit_id) {
        const { data: origHabit } = await client.from('habits').select('*').eq('id', goal.linked_habit_id).eq('user_id', userId).single();
        if (origHabit) {
            const { data: matchingHabit } = await client.from('habits').select('*')
                .eq('cycle_id', newCycleId).eq('user_id', userId).ilike('name', origHabit.name).maybeSingle();
            if (matchingHabit) {
                newGoal.linked_habit_id = matchingHabit.id;
                await client.from('habits').update({ linked_goal_id: newGoal.id }).eq('id', matchingHabit.id);
            } else {
                newGoal.needsHabitLink = true;
                newGoal.originalHabit = origHabit;
            }
        }
    }

    await addGoal(client, userId, newGoal);
    if (newGoal.linked_habit_id) {
        await client.from('goals').update({ linked_habit_id: newGoal.linked_habit_id }).eq('id', newGoal.id);
    }
    return newGoal;
}
